// src/pages/public/QuotationRequest.jsx
// Public customer-details form reached via QR code / share link.
// Saves the visitor's details into the `customers` collection through the
// products-app serverless API (clients cannot write customers directly due to
// firestore.rules). Admins then find these customers in the dashboard's
// Customers tab / quotation builder search.

import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useBranding } from "../../hooks/useBranding";
import { useData } from "../../hooks/useData";
import { B, fonts, Btn } from "../../components/UI";
import SiteFooter from "../../components/SiteFooter";
import {
  getMobileValidationMessage,
  getGSTValidationMessage,
  GST_URP_CODE,
} from "../../utils/validation";

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana",
  "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
  "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

const API_URL = process.env.REACT_APP_PRODUCTS_API_URL || "https://products.nilamsynthetics.com";

const toTitleCase = (value) =>
  String(value || "").replace(/\b\w/g, (c) => c.toUpperCase());

export default function QuotationRequest() {
  const { branding } = useBranding();
  const { categories } = useData();

  const [form, setForm] = useState({
    customer_name: "",
    mobile_number: "",
    firm_name: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    gst_number: "",
    transport_name: "",
    website: "", // honeypot — never shown to humans
  });
  const [errors, setErrors] = useState({});
  const [errorMsg, setErrorMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const validate = () => {
    const next = {};
    if (!String(form.customer_name || "").trim()) next.customer_name = "Customer name is required.";
    const mobileMsg = getMobileValidationMessage(form.mobile_number);
    if (mobileMsg) next.mobile_number = mobileMsg;
    if (!String(form.address || "").trim()) next.address = "Address is required.";
    if (!String(form.city || "").trim()) next.city = "City is required.";
    if (!String(form.state || "").trim()) next.state = "Please select your state.";
    const pincode = String(form.pincode || "").replace(/\D/g, "");
    if (!pincode) next.pincode = "Pincode is required.";
    else if (!/^[1-9][0-9]{5}$/.test(pincode)) next.pincode = "Enter a valid 6-digit pincode.";
    const gstMsg = getGSTValidationMessage(form.gst_number);
    if (gstMsg) next.gst_number = gstMsg;
    return next;
  };
  const handleSubmit = async (event) => {
    event?.preventDefault();
    setErrorMsg("");
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/customers/public-create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer_name: toTitleCase(form.customer_name),
          mobile_number: form.mobile_number,
          firm_name: toTitleCase(form.firm_name),
          address: toTitleCase(form.address),
          city: toTitleCase(form.city),
          state: form.state,
          pincode: form.pincode,
          gst_number: String(form.gst_number || "").toUpperCase(),
          transport_name: toTitleCase(form.transport_name),
          website: form.website, // honeypot
        }),
      });

      if (res.status === 409) {
        setErrorMsg("This mobile number is already registered. Our team already has your details and will get back to you.");
        return;
      }

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErrorMsg(data?.error || "Something went wrong. Please try again.");
        return;
      }

      setSubmitted(true);
    } catch (err) {
      console.error("[QuotationRequest] Submit failed:", err);
      setErrorMsg("Network error. Please check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  const renderFieldError = (key) =>
    errors[key] ? (
      <div style={{ fontSize: 11, color: B.red, marginTop: 4, fontFamily: fonts.body }}>{errors[key]}</div>
    ) : null;

  return (
    <>
      <Helmet>
        <title>Quotation Request | {branding.brandName}</title>
        <meta name="description" content={`Fill in your details on the ${branding.brandName} quotation request form and our team will prepare a quotation for you.`} />
        <meta name="robots" content="noindex" />
      </Helmet>

      <main className="quotation-request-page">
        <div className="quotation-request-container">
          {submitted ? (
            <div className="quotation-request-success" style={{ borderColor: B.green }}>
            <div style={{ fontSize: 52 }}>✅</div>
            <h2 style={{ fontFamily: fonts.display, fontSize: 24, color: B.green, margin: "12px 0 8px" }}>
              Thank you!
            </h2>
            <p style={{ fontFamily: fonts.body, fontSize: 14, color: B.textMid, lineHeight: 1.7, margin: 0 }}>
              Your details have been received. Our team will contact you shortly with your quotation.
            </p>
            <div style={{ marginTop: 24 }}>
              <a href="/" style={{ textDecoration: "none" }}>
                <Btn variant="primary">Back to Home</Btn>
              </a>
            </div>
            </div>
          ) : (
            <form className="quotation-request-form" onSubmit={handleSubmit} noValidate>
            <header className="quotation-request-heading">
              <h1>Where should we send the quote?</h1>
              <p>We'll reply on WhatsApp within a few hours, usually the same day.</p>
            </header>

            {/* Honeypot (hidden) */}
            <input
              type="text"
              value={form.website}
              onChange={(e) => set("website", e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, opacity: 0 }}
            />

            <div className="quotation-request-fields">
              <div className="quotation-request-field">
                <label htmlFor="customer-name">Your name <span aria-hidden="true">*</span></label>
                <input id="customer-name" className="quotation-request-input" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} placeholder="Full name" autoComplete="name" required />
                {renderFieldError("customer_name")}
              </div>
              <div className="quotation-request-field">
                <label htmlFor="firm-name">Shop / business name</label>
                <input id="firm-name" className="quotation-request-input" value={form.firm_name} onChange={(e) => set("firm_name", e.target.value)} placeholder="E.g. Shreeji Garments" autoComplete="organization" />
                {renderFieldError("firm_name")}
              </div>
              <div className="quotation-request-field">
                <label htmlFor="city">City <span aria-hidden="true">*</span></label>
                <input id="city" className="quotation-request-input" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="E.g. Surat" autoComplete="address-level2" required />
                {renderFieldError("city")}
              </div>
              <div className="quotation-request-field">
                <label htmlFor="mobile-number">WhatsApp number <span aria-hidden="true">*</span></label>
                <input id="mobile-number" className="quotation-request-input" value={form.mobile_number} onChange={(e) => set("mobile_number", e.target.value.replace(/[^\d+\- ]/g, "").slice(0, 15))} inputMode="tel" placeholder="10-digit mobile number" autoComplete="tel" required />
                {renderFieldError("mobile_number")}
              </div>
              <div className="quotation-request-field">
                <label htmlFor="address">Full address <span aria-hidden="true">*</span></label>
                <textarea id="address" className="quotation-request-input quotation-request-textarea" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Street, area, landmark..." autoComplete="street-address" required />
                {renderFieldError("address")}
              </div>
              <div className="quotation-request-field quotation-request-two-column">
                <div>
                  <label htmlFor="state">State <span aria-hidden="true">*</span></label>
                  <select id="state" className="quotation-request-input" value={form.state} onChange={(e) => set("state", e.target.value)} autoComplete="address-level1" required>
                    <option value="">Select state</option>
                    {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {renderFieldError("state")}
                </div>
                <div>
                  <label htmlFor="pincode">Pincode <span aria-hidden="true">*</span></label>
                  <input id="pincode" className="quotation-request-input" value={form.pincode} onChange={(e) => set("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="6-digit pincode" autoComplete="postal-code" required />
                  {renderFieldError("pincode")}
                </div>
              </div>
              <div className="quotation-request-field quotation-request-two-column">
                <div>
                  <label htmlFor="gst-number">GST number</label>
                  <div className="quotation-request-gst-input">
                    <input id="gst-number" className="quotation-request-input" value={form.gst_number} onChange={(e) => set("gst_number", e.target.value.toUpperCase())} placeholder="Optional" />
                    <button type="button" onClick={() => set("gst_number", GST_URP_CODE)}>Use URP</button>
                  </div>
                  {renderFieldError("gst_number")}
                </div>
                <div>
                  <label htmlFor="transport-name">Transport name</label>
                  <input id="transport-name" className="quotation-request-input" value={form.transport_name} onChange={(e) => set("transport_name", e.target.value)} placeholder="Optional" />
                  {renderFieldError("transport_name")}
                </div>
              </div>
            </div>

            {errorMsg && (
              <div style={{ background: B.redLight, border: `1px solid ${B.red}`, color: B.red, borderRadius: 8, padding: "10px 14px", fontSize: 13, fontFamily: fonts.body, marginTop: 6 }}>
                ⚠️ {errorMsg}
              </div>
            )}

            <div className="quotation-request-actions">
              <Btn variant="ghost" onClick={() => setForm({
                customer_name: "", mobile_number: "", firm_name: "", address: "",
                city: "", state: "", pincode: "", gst_number: "", transport_name: "", website: "",
              })}>Clear</Btn>
              <Btn variant="primary" type="submit" disabled={saving}>
                {saving ? "Submitting..." : "Submit Details"}
              </Btn>
            </div>
            </form>
          )}
        </div>
      </main>

      <SiteFooter branding={branding} categories={categories} />
    </>
  );
}
